import React from 'react'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import injectSheet from 'react-jss'
import { withRouter } from 'react-router-dom'
import mapSelectorsToProps from 'aria-viewer/core/selectors/mapSelectorsToProps'
import ResultCell from '../components/ResultCell'
import Loader from '../components/Loader'
import { setStopType } from 'aria-viewer/tour/tourActions'
import Analytics from 'aria-viewer/analytics/utils/analytics'
import { getStopByStopId, getCurrentPointIndex } from '../SearchAIUtils'
import { MEDIA_TYPES } from 'aria-viewer/tour/constants'
import { resetSearchAI, hideSearchAIPrompt, setCurrentSearchStop, sendSearchAnalytics } from '../searchAIPromptActions'
import { navigateToStop } from 'aria-viewer/tour/tourActions'

const styleSheet = {
    container: {
        height: ({ isDeviceMobile }) => (isDeviceMobile ? '90%' : '600px'),
        width: ({ isDeviceMobile }) => (isDeviceMobile ? '90%' : '680px'),
        marginLeft: ({ isDeviceMobile }) => (isDeviceMobile ? '' : '102px'),
        padding: '10px 30px',
        background: ({ isDeviceMobile }) => (isDeviceMobile ? '#28334C' : '#fff'),
        transform: 'translateY(0px)',
        borderRadius: '5px',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(155, 155, 155, 0.7)',
            borderRadius: '5px',
        },
    },
    resultsContainer: {
        display: 'flex',
        gap: '24px',
        flexWrap: 'wrap',
        marginBottom: '10px',
    },
    results: {
        fontFamily: 'CircularStd, Circular Std Book',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '24px',
        color: ({ isDeviceMobile }) => (isDeviceMobile ? '#fff' : 'rgba(99, 115, 164, 1)'),
        marginBottom: '10px',
    },
}

const SearchResultsContainer = ({
    searchLoading,
    classes,
    results,
    setStopType,
    stopsList,
    stopsToPoints,
    pointsRawList,
    trailsList,
    resetSearchAI,
    hideSearchAIPrompt,
    isDeviceMobile,
    navigateToStop,
    setCurrentSearchStop,
    tour,
    sendSearchAnalytics,
}) => {
    const handleResultClick = (e, result) => {
        e.stopPropagation()
        // sets stop details for analytics web socket
        setStopType(Analytics.personalizedTypes.SEARCH)
        setCurrentSearchStop(result)

        // parse url and update/add loc and media params
        const url = new URL(window.location.href)
        const params = new URLSearchParams(url.search)

        // deletes loc and media params if it already exists
        params.delete('loc')
        params.delete('media')

        let mediaParams = ''
        if (result.media_type !== MEDIA_TYPES.stop) {
            mediaParams = `${result.media_type}:${result.media_id}:${result.stop_id}`
        }

        const stop = getStopByStopId(result.stop_id, stopsList)
        const pointIndex = getCurrentPointIndex(trailsList, pointsRawList, stop)
        const trailIndex = trailsList.findIndex(trail => trail.trailid === result.trail_id)

        const locParams = `${result.trail_id}:${pointIndex}:1:1`

        let newUrl = `${url.pathname}`
        if (url.search.length > 0) {
            newUrl += `?${params.toString()}`
            // need to set params AFTER toString() due to colons (':') in locParams and mediaParams
            params.set('loc', locParams)
            newUrl += `&loc=${params.get('loc')}`
        } else {
            params.set('loc', locParams)
            newUrl += `?loc=${params.get('loc')}`
        }
        if (mediaParams) {
            params.set('media', mediaParams)
            newUrl += `&media=${params.get('media')}`
        }
        // replaces new url without navigating to avoid refreshing
        window.history.pushState(null, null, newUrl)

        // navigates to stop and/or media
        navigateToStop(
            pointIndex,
            trailIndex,
            result.media_id,
            result.media_type,
            result.stop_id,
            stopsList,
            stopsToPoints,
            isDeviceMobile,
            tour.type
        )

        // hiding search AI prompt reduces modal stack and allows audio to play when navigating to next stop
        hideSearchAIPrompt()
        resetSearchAI()

        // send search details to analytics endpoint
        sendSearchAnalytics(result)
    }

    return (
        <div className={classes.container}>
            {searchLoading ? (
                <Loader isDeviceMobile={isDeviceMobile} />
            ) : (
                <>
                    <div className={classes.results}>
                        {results?.filter(result => result.media != null).length} Results
                    </div>
                    <div className={classes.resultsContainer} onClick={e => e.stopPropagation()}>
                        {results?.map(result => (
                            <ResultCell
                                key={result.id}
                                result={result}
                                stop={getStopByStopId(result.stop_id, stopsList)}
                                media={result.media}
                                onResultClick={handleResultClick}
                                isDeviceMobile={isDeviceMobile}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

export default compose(
    injectSheet(styleSheet),
    connect(
        mapSelectorsToProps([
            'searchLoading',
            'stopsList',
            'stopsToPoints',
            'pointsRawList',
            'trailsList',
            'tour',
            'mediaListByType',
            'isDeviceMobile',
        ]),
        {
            setStopType,
            resetSearchAI,
            hideSearchAIPrompt,
            navigateToStop,
            setCurrentSearchStop,
            sendSearchAnalytics,
        }
    ),
    withRouter
)(SearchResultsContainer)
